// Country code selector component with flags
import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

// Common country codes with flags
const COUNTRIES = [
  { code: "+33", country: "FR", flag: "🇫🇷", name: "France", lang: "fr" },
  { code: "+44", country: "GB", flag: "🇬🇧", name: "United Kingdom", lang: "en" },
  { code: "+1", country: "US", flag: "🇺🇸", name: "United States", lang: "en" },
  { code: "+49", country: "DE", flag: "🇩🇪", name: "Germany", lang: "en" },
  { code: "+39", country: "IT", flag: "🇮🇹", name: "Italy", lang: "en" },
  { code: "+34", country: "ES", flag: "🇪🇸", name: "Spain", lang: "en" },
  { code: "+31", country: "NL", flag: "🇳🇱", name: "Netherlands", lang: "en" },
  { code: "+32", country: "BE", flag: "🇧🇪", name: "Belgium", lang: "fr" },
  { code: "+41", country: "CH", flag: "🇨🇭", name: "Switzerland", lang: "fr" },
  { code: "+351", country: "PT", flag: "🇵🇹", name: "Portugal", lang: "en" },
  { code: "+48", country: "PL", flag: "🇵🇱", name: "Poland", lang: "en" },
  { code: "+30", country: "GR", flag: "🇬🇷", name: "Greece", lang: "en" },
  { code: "+45", country: "DK", flag: "🇩🇰", name: "Denmark", lang: "en" },
  { code: "+46", country: "SE", flag: "🇸🇪", name: "Sweden", lang: "en" },
  { code: "+47", country: "NO", flag: "🇳🇴", name: "Norway", lang: "en" },
  { code: "+358", country: "FI", flag: "🇫🇮", name: "Finland", lang: "en" },
  { code: "+63", country: "PH", flag: "🇵🇭", name: "Philippines", lang: "en" },
  { code: "+91", country: "IN", flag: "🇮🇳", name: "India", lang: "en" },
  { code: "+86", country: "CN", flag: "🇨🇳", name: "China", lang: "en" },
  { code: "+81", country: "JP", flag: "🇯🇵", name: "Japan", lang: "en" },
  { code: "+7", country: "RU", flag: "🇷🇺", name: "Russia", lang: "en" },
  { code: "+380", country: "UA", flag: "🇺🇦", name: "Ukraine", lang: "en" },
  { code: "+55", country: "BR", flag: "🇧🇷", name: "Brazil", lang: "en" },
  { code: "+52", country: "MX", flag: "🇲🇽", name: "Mexico", lang: "en" },
  { code: "+90", country: "TR", flag: "🇹🇷", name: "Turkey", lang: "en" },
  { code: "+20", country: "EG", flag: "🇪🇬", name: "Egypt", lang: "en" },
  { code: "+212", country: "MA", flag: "🇲🇦", name: "Morocco", lang: "fr" },
  { code: "+216", country: "TN", flag: "🇹🇳", name: "Tunisia", lang: "fr" },
  { code: "+213", country: "DZ", flag: "🇩🇿", name: "Algeria", lang: "fr" },
  { code: "+966", country: "SA", flag: "🇸🇦", name: "Saudi Arabia", lang: "en" },
  { code: "+971", country: "AE", flag: "🇦🇪", name: "UAE", lang: "en" },
];

// Get country from phone number
export const getCountryFromPhone = (phone) => {
  if (!phone) return COUNTRIES[0]; // Default to France
  
  const cleanPhone = phone.replace(/\s/g, '');
  
  // Sort by code length (longest first) to match more specific codes first
  const sortedCountries = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);
  
  for (const country of sortedCountries) {
    if (cleanPhone.startsWith(country.code)) {
      return country;
    }
  }
  
  return COUNTRIES[0]; // Default to France
};

// Get language from country code
export const getLanguageFromCountryCode = (countryCode) => {
  const country = COUNTRIES.find(c => c.code === countryCode);
  return country ? country.lang : "en";
};

export default function PhoneInput({ 
  value, 
  onChange, 
  onCountryChange,
  placeholder = "Phone number",
  className = "",
  disabled = false 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  
  // Parse current value to extract country and number
  const currentCountry = getCountryFromPhone(value);
  const numberWithoutCode = value ? value.replace(currentCountry.code, '').trim() : '';
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Filter countries by search
  const filteredCountries = COUNTRIES.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.code.includes(searchQuery) ||
    c.country.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleCountrySelect = (country) => {
    const newPhone = country.code + numberWithoutCode;
    onChange(newPhone);
    if (onCountryChange) {
      onCountryChange(country);
    }
    setIsOpen(false);
    setSearchQuery("");
  };
  
  const handleNumberChange = (e) => {
    let input = e.target.value;
    // Only allow digits
    input = input.replace(/[^\d]/g, '');
    const newPhone = currentCountry.code + input;
    onChange(newPhone);
  };
  
  return (
    <div className={`flex ${className}`} ref={dropdownRef}>
      {/* Country selector */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`flex items-center gap-1 px-2 sm:px-3 py-2 border border-r-0 rounded-l-md bg-slate-50 hover:bg-slate-100 transition-colors h-10 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          disabled={disabled}
        >
          <span className="text-lg">{currentCountry.flag}</span>
          <span className="text-xs sm:text-sm text-slate-600 hidden sm:inline">{currentCountry.code}</span>
          <ChevronDown className="w-3 h-3 text-slate-400" />
        </button>
        
        {/* Dropdown */}
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-white border rounded-lg shadow-lg z-50 max-h-64 overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b">
              <input
                type="text"
                placeholder="Search country..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                autoFocus
              />
            </div>
            
            {/* Country list */}
            <div className="overflow-y-auto max-h-48">
              {filteredCountries.map((country) => (
                <button
                  key={country.code + country.country}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-orange-50 transition-colors text-left ${
                    currentCountry.code === country.code ? 'bg-orange-100' : ''
                  }`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="flex-1 text-sm">{country.name}</span>
                  <span className="text-xs text-slate-500">{country.code}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Phone number input */}
      <input
        type="tel"
        value={numberWithoutCode}
        onChange={handleNumberChange}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 px-3 py-2 border rounded-r-md focus:outline-none focus:ring-2 focus:ring-orange-500 h-10 min-w-0"
      />
    </div>
  );
}

// Export countries for use elsewhere
export { COUNTRIES };
